import { apiClient, type Paginated } from '@/core/api';

/** The quote line of a reply (BE ADR-112): who it answers and a one-line
 *  plain-text excerpt (mentions read as @Name) — or, once that comment is gone,
 *  only `is_deleted`. */
export type CommentReplyPreview =
  | {
      is_deleted: false;
      id: number;
      author_name: string | null;
      author_photo: string | null;
      excerpt: string;
    }
  | { is_deleted: true; id: null; author_name: null; author_photo: null; excerpt: '' };

export interface Comment {
  id: number;
  author: number | null;
  /** Author's display name (server-resolved). */
  author_name: string | null;
  /** Author's avatar URL (HRM photo → account avatar), or null for initials. */
  author_photo: string | null;
  body: string;
  is_pinned: boolean;
  /** The comment this one replies to — null for a plain comment, or once that comment was deleted. */
  reply_to: number | null;
  reply_to_preview: CommentReplyPreview | null;
  content_type: number;
  object_id: string;
  created: string;
  modified: string;
}

export interface CommentInput {
  content_type: number;
  object_id: string;
  body: string;
  /** Post as a reply to this comment (same item only; fixed once posted). */
  reply_to?: number | null;
}

const BASE = '/collab/comments/';

export interface CommentListParams {
  page?: number;
  page_size?: number;
  content_type?: number;
  object_id?: string;
}

export async function listComments(params: CommentListParams = {}): Promise<Paginated<Comment>> {
  const { data } = await apiClient.get<Paginated<Comment>>(BASE, { params });
  return data;
}

export async function createComment(input: CommentInput): Promise<Comment> {
  const { data } = await apiClient.post<Comment>(BASE, input);
  return data;
}

export async function updateComment(id: number, body: string): Promise<Comment> {
  const { data } = await apiClient.patch<Comment>(`${BASE}${id}/`, { body });
  return data;
}

/** Toggle pinned state (server flips it). Author or org admin only. */
export async function pinComment(id: number): Promise<Comment> {
  const { data } = await apiClient.post<Comment>(`${BASE}${id}/pin/`);
  return data;
}

export async function deleteComment(id: number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

// ---- Content-type resolver (model label → id) ----
export interface ContentTypeRef {
  id: number;
  app_label: string;
  model: string;
}

export async function resolveContentType(modelLabel: string): Promise<ContentTypeRef> {
  const { data } = await apiClient.get<ContentTypeRef>('/common/content-types/', {
    params: { model: modelLabel },
  });
  return data;
}
